import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import InputMask from "react-input-mask";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OrganizationSelect from "./OrganizationSelect";
import { validateContact, ContactErrors } from "./contact.validation";
import { CountryCode, DrawerState, Organization } from "./contact.types";

interface Props {
    open: boolean;
    state: DrawerState;
    countryCodes: CountryCode[];
    onChange: (updater: (s: DrawerState) => DrawerState) => void;
    onClose: () => void;
    onSave: () => void;
    onEditFlags: () => void;
    onInactiveCountry: (msg: string) => void;
}

const popperSx = { popper: { sx: { zIndex: 20000 } } };

export default function ContactFormDrawer({
    open,
    state,
    countryCodes,
    onChange,
    onClose,
    onSave,
    onEditFlags,
    onInactiveCountry,
}: Props) {
    const isEdit = !!state.id;
    const [errors, setErrors] = React.useState<ContactErrors>({});

    React.useEffect(() => {
        if (open) setErrors({});
    }, [open]);

    const handleSaveClick = () => {
        const e = validateContact(state);
        setErrors(e);
        if (Object.keys(e).length === 0) onSave();
    };

    const clearErr = (k: keyof ContactErrors) =>
        setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));

    const countryOptions = countryCodes.map((c) => ({ label: c.value, value: c.value }));
    const selectedCountry =
        countryOptions.find((o) => o.label === state.countryCode) || null;

    return (
        <>
            <div className={`ctpl-overlay${open ? " open" : ""}`} onClick={onClose} />
            <div className={`ctpl-drawer ctpl-drawer-wide${open ? " open" : ""}`}>
                <div className="ctpl-drawer-header">
                    <h2>{isEdit ? `Edit — ${state.name}` : "Add Contact"}</h2>
                    <button className="ctpl-icon-btn" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                <div className="ctpl-drawer-body">
                    {/* Identity */}
                    <div className="ctpl-field-group">
                        <div className="ctpl-section-label">Contact info</div>

                        <label className="ctpl-field-label">
                            Salutation <span className="ctpl-required">*</span>
                        </label>
                        <Select
                            fullWidth
                            size="small"
                            displayEmpty
                            value={state.salutation}
                            error={!!errors.salutation}
                            onChange={(e) => {
                                onChange((s) => ({ ...s, salutation: e.target.value }));
                                clearErr("salutation");
                            }}
                            sx={{ mb: errors.salutation ? 0.5 : 1.5 }}
                            MenuProps={{ sx: { zIndex: 20000 } }}
                        >
                            <MenuItem value="" disabled>Select</MenuItem>
                            <MenuItem value="Mr">Mr</MenuItem>
                            <MenuItem value="Mrs">Mrs</MenuItem>
                            <MenuItem value="Ms">Ms</MenuItem>
                        </Select>
                        {errors.salutation && <div className="ctpl-field-error">{errors.salutation}</div>}

                        <label className="ctpl-field-label" style={{ marginTop: 12 }}>
                            Name <span className="ctpl-required">*</span>
                        </label>
                        <input
                            className={`ctpl-field-input${errors.name ? " error" : ""}`}
                            placeholder="Full name"
                            value={state.name}
                            onChange={(e) => {
                                const v = e.target.value;
                                onChange((s) => ({ ...s, name: v }));
                                if (v.trim()) clearErr("name");
                            }}
                        />
                        {errors.name && <div className="ctpl-field-error">{errors.name}</div>}

                        <label className="ctpl-field-label" style={{ marginTop: 12 }}>
                            Organization <span className="ctpl-required">*</span>
                        </label>
                        <OrganizationSelect
                            value={state.organization}
                            error={!!errors.organization}
                            onChange={(org: Organization | null) => {
                                onChange((s) => ({ ...s, organization: org }));
                                if (org) clearErr("organization");
                            }}
                        />
                        {errors.organization && <div className="ctpl-field-error">{errors.organization}</div>}
                    </div>

                    {errors.channels && (
                        <div className="ctpl-field-error" style={{ marginTop: -12, marginBottom: 12 }}>
                            {errors.channels}
                        </div>
                    )}

                    {/* Email */}
                    <div className="ctpl-field-group">
                        <div className="ctpl-section-label">✉️ Email</div>
                        <label className="ctpl-field-label">Email address</label>
                        <input
                            className={`ctpl-field-input${errors.email ? " error" : ""}`}
                            type="email"
                            placeholder="name@example.com"
                            value={state.email}
                            onChange={(e) => {
                                const v = e.target.value;
                                onChange((s) => ({ ...s, email: v }));
                                clearErr("email");
                                clearErr("channels");
                            }}
                        />
                        {errors.email && <div className="ctpl-field-error">{errors.email}</div>}
                    </div>

                    {/* WhatsApp */}
                    <div className="ctpl-field-group">
                        <div className="ctpl-section-label">💬 WhatsApp</div>

                        <label className="ctpl-field-label">Country code</label>
                        <Autocomplete
                            options={countryOptions}
                            value={selectedCountry}
                            getOptionLabel={(o) => `+${o.label}`}
                            slotProps={popperSx}
                            onChange={(_e, newVal) => {
                                const value = newVal?.value || "";
                                const cc = countryCodes.find((c) => c.calling_code === value);
                                if (cc && !cc.whatsapp_status) {
                                    onInactiveCountry(
                                        "The selected country code is not active for WhatsApp messages. If you wish to get this country activated, reach out to us over email."
                                    );
                                    return;
                                }
                                onChange((s) => ({ ...s, countryCode: value }));
                                clearErr("countryCode");
                            }}
                            renderInput={(params) => (
                                <TextField {...params} size="small" variant="outlined" placeholder="Select country code" error={!!errors.countryCode} />
                            )}
                        />
                        {errors.countryCode && <div className="ctpl-field-error">{errors.countryCode}</div>}

                        <label className="ctpl-field-label" style={{ marginTop: 12 }}>WhatsApp number</label>
                        <InputMask
                            mask="9999999999"
                            maskChar={null}
                            value={state.whatappNumber}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const v = e.target.value;
                                onChange((s) => ({ ...s, whatappNumber: v }));
                                clearErr("whatappNumber");
                                clearErr("channels");
                            }}
                        >
                            {(inputProps: any) => (
                                <input
                                    {...inputProps}
                                    className={`ctpl-field-input${errors.whatappNumber ? " error" : ""}`}
                                    placeholder="10-digit number"
                                />
                            )}
                        </InputMask>
                        {errors.whatappNumber && <div className="ctpl-field-error">{errors.whatappNumber}</div>}
                    </div>

                    {/* Flags */}
                    <div className="ctpl-field-group">
                        <div className="ctpl-section-label">🏷 Flags <span className="ctpl-optional">(optional)</span></div>
                        <button type="button" className="ctpl-flags-field" onClick={onEditFlags}>
                            {state.flags.length ? (
                                state.flags.map((f: any, i: number) => (
                                    <span className="ctpl-chip ctpl-flag-chip-selected" key={f?._id || i}>
                                        {f?.category ? `${f.category}: ${f.name}` : f?.name}
                                        <span
                                            className="ctpl-chip-remove"
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onChange((s) => ({ ...s, flags: s.flags.filter((_, idx) => idx !== i) }));
                                            }}
                                        >
                                            ×
                                        </span>
                                    </span>
                                ))
                            ) : (
                                <span className="ctpl-chip-placeholder">Click to select flags</span>
                            )}
                        </button>
                    </div>

                    {/* {isEdit && (
                        <div className="ctpl-toggle-row">
                            <div>
                                <div style={{ fontSize: 13.5, fontWeight: 600 }}>Active</div>
                                <div className="ctpl-hint">Inactive contacts won't receive alerts.</div>
                            </div>
                            <button
                                type="button"
                                className={`ctpl-switch${state.isActive ? " on" : ""}`}
                                onClick={() => onChange((s) => ({ ...s, isActive: !s.isActive }))}
                            />
                        </div>
                    )} */}
                </div>

                <div className="ctpl-drawer-footer">
                    <button className="ctpl-btn ctpl-btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="ctpl-btn ctpl-btn-primary" onClick={handleSaveClick}>
                        {isEdit ? "Save changes" : "Save contact"}
                    </button>
                </div>
            </div>
        </>
    );
}